import {
    DEFAULT_LANGUAGE,
    SUPPORTED_LANGUAGES,
    SupportedLanguage,
} from './constants';

/**
 * Normalizes browser language codes to supported formats
 * @param langCode - Browser language code (e.g., 'ja', 'en', 'ja-JP')
 * @returns Normalized language code or undefined if not supported
 */
export const normalizeBrowserLanguage = (
    langCode: string,
): string | undefined => {
    if (!langCode) return undefined;

    const lowerLang = langCode.toLowerCase();

    // Exact match
    if (SUPPORTED_LANGUAGES.includes(langCode as SupportedLanguage)) {
        return langCode;
    }

    // Partial match: 'ja' -> 'ja-JP', 'en' -> 'en-US'
    if (lowerLang.startsWith('ja')) {
        return 'ja-JP';
    }
    if (lowerLang.startsWith('en')) {
        return 'en-US';
    }

    return undefined;
};

/**
 * Custom browser language detector with normalization
 * Detects browser language and normalizes it to supported formats
 */
const browserLanguageDetector = {
    name: 'navigator',
    type: 'languageDetector',

    lookup: () => {
        try {
            if (typeof navigator !== 'undefined' && navigator.language) {
                const normalized = normalizeBrowserLanguage(navigator.language);
                if (normalized) {
                    return normalized;
                }
            }
        } catch (error) {
            // Silently fail if navigator is not available
        }
        return undefined;
    },

    cacheUserLanguage: () => {
        // No-op: browser language is detected on each load
    },
};

/**
 * Checks if user is authenticated based on localStorage mvc-user
 * @returns true if user is authenticated, false otherwise
 */
const isUserAuthenticated = (): boolean => {
    try {
        const userString = localStorage.getItem('mvc-user');
        if (userString) {
            const user = JSON.parse(userString);
            // Check if user is authenticated (has valid timestamp)
            const sessionExpired = user
                ? Date.now() > (user.timestamp || 0)
                : true;
            return !sessionExpired && !!user;
        }
    } catch (error) {
        // Silently fail if localStorage is not available or parsing fails
    }
    return false;
};

/**
 * Checks if current route is the login route
 * @returns true if on /login route, false otherwise
 */
const isLoginRoute = (): boolean => {
    try {
        if (typeof window !== 'undefined' && window.location) {
            const pathname = window.location.pathname;
            return pathname === '/login' || pathname.endsWith('/login');
        }
    } catch (error) {
        // Silently fail if window is not available
    }
    return false;
};

/**
 * Guest user language detector
 * Only applies if user is not authenticated or on the /login route
 * Resolution order:
 * 1. localStorage i18nextLng (if set and supported)
 * 2. browser language (if supported)
 * 3. fallback to DEFAULT_LANGUAGE
 */
const guestUserLanguageDetector = {
    name: 'guestUser',
    type: 'languageDetector',
    lookup: () => {
        // Only apply if user is not authenticated or on login route
        const authenticated = isUserAuthenticated();
        const onLoginRoute = isLoginRoute();
        if (authenticated && !onLoginRoute) {
            // User is authenticated and not on login route, skip guest detector
            return undefined;
        }

        // 1. Check localStorage i18nextLng first
        const i18nextLng = localStorage.getItem('i18nextLng');
        if (
            i18nextLng &&
            SUPPORTED_LANGUAGES.includes(i18nextLng as SupportedLanguage)
        ) {
            return i18nextLng;
        }
        // 2. Fall back to browser language if localStorage not set or not supported
        const browserLanguage = browserLanguageDetector.lookup();
        if (browserLanguage) {
            return browserLanguage;
        }
        // 3. Fall back to default language
        return DEFAULT_LANGUAGE;
    },
};

/**
 * Authenticated user language detector
 * Resolution order:
 * 1. User settings (userPreferences.language)
 * 2. localStorage i18nextLng (if set and supported)
 * 3. Browser language (if supported)
 * 4. Fallback to DEFAULT_LANGUAGE
 */
const authenticatedUserLanguageDetector = {
    name: 'authenticatedUser',
    type: 'languageDetector',

    lookup: () => {
        try {
            const userString = localStorage.getItem('mvc-user');
            if (userString) {
                const user = JSON.parse(userString);
                // Check if user is authenticated (has valid timestamp)
                const sessionExpired = user
                    ? Date.now() > (user.timestamp || 0)
                    : true;
                if (!sessionExpired && user) {
                    // 1. Check user settings first
                    const language = user?.userPreferences?.language;
                    if (language && SUPPORTED_LANGUAGES.includes(language)) {
                        return language;
                    }
                    // 2. Fall back to localStorage i18nextLng if user settings not available or not supported
                    const i18nextLng = localStorage.getItem('i18nextLng');
                    if (
                        i18nextLng &&
                        SUPPORTED_LANGUAGES.includes(
                            i18nextLng as SupportedLanguage,
                        )
                    ) {
                        return i18nextLng;
                    }
                    // 3. Fall back to browser language
                    const browserLanguage = browserLanguageDetector.lookup();
                    if (browserLanguage) {
                        return browserLanguage;
                    }
                    // 4. Fall back to default language
                    return DEFAULT_LANGUAGE;
                }
            }
        } catch (error) {
            // Silently fail if localStorage is not available or parsing fails
        }
        return undefined;
    },

    cacheUserLanguage: () => {
        // No-op: userPreferences are managed by the backend API
        // We don't want to cache here as it's already in localStorage under 'mvc-user'
    },
};

export {
    authenticatedUserLanguageDetector,
    browserLanguageDetector,
    guestUserLanguageDetector,
};
